import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Slider,
  Chip,
  Divider
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPlus,
  faEdit,
  faCalendar,
  faUser,
  faChartLine,
  faExclamationTriangle,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import { getAlldata, getDataWithFilterUrl } from "../../controllers/APIController";
import { createTask } from "../../controllers/TaskController";
import { toast } from 'react-hot-toast';

const CreateTaskModal = ({ isOpen, onClose, onSuccess, preSelectedMetric = null, editMode = false, existingTask = null }) => {
  const [formData, setFormData] = useState({
    Name: "",
    Description: "",
    Status: "not_started",
    Priority: "medium",
    metric: preSelectedMetric?.id || "",
    StartDate: new Date().toISOString().split('T')[0], // Today as default
    EstimatedEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Next week as default
    Progress: 0,
    RiskLevel: "low",
    Dependencies: [],
    Tags: [],
    Comments: "",
    // Simple workflow linking (optional)
    workflowId: ""
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [users, setUsers] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [dependencySearchTerm, setDependencySearchTerm] = useState("");
  const [dataLoading, setDataLoading] = useState(true);

  // Load metrics and users on component mount
  useEffect(() => {
    if (isOpen) {
      setDataLoading(true);
      Promise.all([
        loadMetrics(),
        loadUsers(),
        loadWorkflows(),
        loadAvailableTasks()
      ]).finally(() => {
        setDataLoading(false);
      });
    }
  }, [isOpen]);

    // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editMode && existingTask) {
        // Edit mode: populate form with existing task data
        const taskData = existingTask.attributes || existingTask;
        setFormData({
          Name: taskData.Name || taskData.name || "",
          Description: taskData.Description || taskData.description || "",
          Status: taskData.Status || "not_started",
          Priority: taskData.Priority || "medium",
          metric: taskData.metric?.id || taskData.metric || "",
          workflowId: taskData.workflowId || "",
          StartDate: taskData.StartDate || new Date().toISOString().split('T')[0],
          EstimatedEndDate: taskData.EstimatedEndDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          Progress: taskData.Progress || 0,
          RiskLevel: taskData.RiskLevel || "low",
          Dependencies: taskData.Dependencies || [],
          Tags: taskData.Tags || taskData.tags || [],
          Comments: taskData.Comments || taskData.comments || ""
        });
      } else {
        // Create mode: reset to defaults
        setFormData({
          Name: "",
          Description: "",
          Status: "not_started",
          Priority: "medium",
          metric: preSelectedMetric?.id || "",
          StartDate: new Date().toISOString().split('T')[0], // Today as default
          EstimatedEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Next week as default
          Progress: 0,
          RiskLevel: "low",
          Dependencies: [],
          Tags: [],
          Comments: "",
          workflowId: ""
        });
      }
      setErrors({});
      setDependencySearchTerm("");
    }
  }, [isOpen, preSelectedMetric, editMode, existingTask]);

  const loadMetrics = async () => {
    try {
      // ✅ CORRECT: Get ALL metrics directly from metrics collection
      const response = await getAlldata("metrics");
      console.log("Metrics API response:", response);
      
      let metricsData = [];
      if (response && Array.isArray(response)) {
        metricsData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        metricsData = response.data;
      } else if (response && response.data && Array.isArray(response.data.data)) {
        metricsData = response.data.data;
      }

      if (Array.isArray(metricsData) && metricsData.length > 0) {
        // Process metrics data to extract the correct structure
        const processedMetrics = metricsData.map(metric => {
          // Handle different possible data structures
          if (metric.attributes) {
            // Strapi-style response
            return {
              id: metric.id,
              Name: metric.attributes.Name || metric.attributes.name || 'Unnamed Metric',
              description: metric.attributes.Description || metric.attributes.description || 'No description available'
            };
          } else if (metric.Name || metric.name) {
            // Direct properties
            return {
              id: metric.id,
              Name: metric.Name || metric.name || 'Unnamed Metric',
              description: metric.Description || metric.description || 'No description available'
            };
          } else {
            // Fallback
            return {
              id: metric.id,
              Name: 'Unnamed Metric',
              description: 'No description available'
            };
          }
        });

        console.log("Processed metrics:", processedMetrics);
        setMetrics(processedMetrics);
      } else {
        console.log("No metrics found");
        setMetrics([]);
      }
    } catch (error) {
      console.error("Error loading metrics:", error);
    }
  };

  const loadUsers = async () => {
    try {
      console.log("🔍 Loading users with proper authentication...");
      
      // Get JWT token from session storage
      const userLogged = JSON.parse(sessionStorage.getItem("userData"));
      const jwt = userLogged?.jwt;
      
      if (!jwt) {
        console.warn('🔍 No JWT token found, trying alternative authentication...');
        // Try alternative JWT storage
        const jwtTesting = JSON.parse(sessionStorage.getItem("jwtTesting"));
        const altJwt = jwtTesting?.jwt;
        if (altJwt) {
          console.log('🔍 Using alternative JWT token');
        } else {
          console.error('🔍 No JWT token available for user authentication');
          setUsers([]);
          return;
        }
      }

      const response = await getDataWithFilterUrl('users', jwt);
      if (response.success && response.data) {
        const mappedUsers = response.data.map(user => ({
          ...user,
          displayName: user.firstName && user.lastName 
            ? `${user.firstName} ${user.lastName}` 
            : user.username || user.email || `User ${user.id}`
        }));
        setUsers(mappedUsers);
        console.log('✅ Users fetched successfully:', mappedUsers.length);
      } else {
        console.warn('⚠️ No users found or invalid response');
        setUsers([]);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      toast.error('Failed to fetch users');
      setUsers([]);
    }
  };

  const loadWorkflows = async () => {
    try {
      const response = await getDataWithFilterUrl('workflows');
      if (response.success) {
        setWorkflows(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
      toast.error('Failed to fetch workflows');
    }
  };

  const handleWorkflowChange = (workflowId) => {
      setFormData(prev => ({
        ...prev,
      workflowId: workflowId
    }));
  };



  const loadAvailableTasks = async () => {
    try {
      const response = await getAlldata("tasks");
      console.log("Available tasks API response:", response);
      
      let tasksData = [];
      if (response && response.data) {
        tasksData = response.data;
      } else if (response && Array.isArray(response)) {
        tasksData = response;
      } else if (response && response.data && Array.isArray(response.data.data)) {
        tasksData = response.data.data;
      }

      if (Array.isArray(tasksData) && tasksData.length > 0) {
        // Process tasks data to extract the correct structure
        const processedTasks = tasksData.map(task => {
          // Handle different possible data structures
          if (task.attributes) {
            // Strapi-style response
            return {
              id: task.id,
              Name: task.attributes.Name || task.attributes.name || 'Unnamed Task',
              description: task.attributes.Description || task.attributes.description || 'No description available'
            };
          } else if (task.Name || task.name) {
            // Direct properties
            return {
              id: task.id,
              Name: task.Name || task.name || 'Unnamed Task',
              description: task.Description || task.description || 'No description available'
            };
          } else {
            // Fallback
            return {
              id: task.id,
              Name: 'Unnamed Task',
              description: 'No description available'
            };
          }
        });

        console.log("Processed available tasks:", processedTasks);
        setAvailableTasks(processedTasks);
      } else {
        console.log("No tasks found for dependencies");
        setAvailableTasks([]);
      }
    } catch (error) {
      console.error("Error loading available tasks:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleDateChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear previous errors for this field
    setErrors(prev => ({ ...prev, [field]: "" }));
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(value);
    
    // Validate past dates
    if (field === 'StartDate' && selectedDate < today) {
      setErrors(prev => ({ ...prev, StartDate: "Start date cannot be in the past" }));
      return;
    }
    
    if (field === 'EstimatedEndDate' && selectedDate < today) {
      setErrors(prev => ({ ...prev, EstimatedEndDate: "End date cannot be in the past" }));
      return;
    }
    
    // Validate date logic
    if (field === 'EstimatedEndDate' && formData.StartDate && value < formData.StartDate) {
      setErrors(prev => ({ ...prev, EstimatedEndDate: "End date must be after start date" }));
    } else if (field === 'StartDate' && formData.EstimatedEndDate && value > formData.EstimatedEndDate) {
      setErrors(prev => ({ ...prev, StartDate: "Start date must be before end date" }));
    }
  };

  const handleTagInput = (value) => {
    if (value && !(formData.Tags || []).includes(value)) {
      setFormData(prev => ({ ...prev, Tags: [...(prev.Tags || []), value] }));
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, Tags: (prev.Tags || []).filter(tag => tag !== tagToRemove) }));
  };

  const handleDependencyChange = (taskId) => {
    setFormData(prev => ({
      ...prev,
      Dependencies: (prev.Dependencies || []).includes(taskId)
        ? (prev.Dependencies || []).filter(id => id !== taskId)
        : [...(prev.Dependencies || []), taskId]
    }));
  };

  // Filter tasks based on search term
  const filteredDependencyTasks = availableTasks.filter(task => 
    task.Name?.toLowerCase().includes(dependencySearchTerm.toLowerCase()) ||
    task.description?.toLowerCase().includes(dependencySearchTerm.toLowerCase())
  );

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.Name.trim()) newErrors.Name = "Task name is required";
    if (!formData.Description.trim()) newErrors.Description = "Description is required";
    // Metric is now optional - no validation needed
    
    // ✅ Required dates (as per createTask function requirements)
    if (!formData.StartDate) newErrors.StartDate = "Start date is required";
    if (!formData.EstimatedEndDate) newErrors.EstimatedEndDate = "Estimated end date is required";

    // Date validation
    if (formData.StartDate && formData.EstimatedEndDate && formData.StartDate > formData.EstimatedEndDate) {
      newErrors.EstimatedEndDate = "End date must be after start date";
    }
    
    // Past date validation
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (formData.StartDate && new Date(formData.StartDate) < today) {
      newErrors.StartDate = "Start date cannot be in the past";
    }
    
    if (formData.EstimatedEndDate && new Date(formData.EstimatedEndDate) < today) {
      newErrors.EstimatedEndDate = "End date cannot be in the past";
    }
    
    // No workflow validation needed - workflow linking is optional



    console.log("Form validation result:", { newErrors, isValid: Object.keys(newErrors).length === 0 });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Prepare task data for the API
      const taskData = {
        Name: formData.Name,
        Description: formData.Description,
        Status: formData.Status,
        Priority: formData.Priority,
        metric: formData.metric,
        workflowId: formData.workflowId, // Optional workflow linking
        StartDate: formData.StartDate,
        EstimatedEndDate: formData.EstimatedEndDate,
        Progress: formData.Progress,
        RiskLevel: formData.RiskLevel,
        Dependencies: formData.Dependencies,
        Tags: formData.Tags,
        Comments: formData.Comments
      };

      if (editMode && existingTask) {
        // Edit mode: Update existing task
        console.log("Updating task with data:", taskData);
        
        // TODO: Implement update API call
        // For now, just simulate success
        console.log("Task updated successfully (simulated)");
        setErrors({ submit: "Task updated successfully!" });
      } else {
        // Create mode: Create new task
        console.log("Creating task with data:", taskData);
        
        // ✅ REAL API CALL: Create the task using TaskController
        console.log("Calling createTask function...");
        const createdTask = await createTask(taskData);
        
        console.log("Task created successfully:", createdTask);
        
        // Task created successfully
        setErrors({ submit: "Task created successfully!" });
      }
      
      // Wait a moment to show success message, then close
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error(editMode ? "Error updating task:" : "Error creating task:", error);
      setErrors({ submit: `Failed to ${editMode ? 'update' : 'create'} task: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';

      case 'not_started': return 'default';
      case 'on_hold': return 'danger';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        base: "bg-white",
        header: "border-b border-neutral-200 pb-4",
        body: "py-6",
        footer: "border-t border-neutral-200 pt-4"
      }}
    >
      <ModalContent>
                 <ModalHeader className="flex items-center gap-3">
           <div className={`p-2 rounded-lg ${editMode ? 'bg-info-100' : 'bg-primary-100'}`}>
             <FontAwesomeIcon 
               icon={editMode ? faEdit : faPlus} 
               className={`w-5 h-5 ${editMode ? 'text-info-600' : 'text-primary-600'}`} 
             />
           </div>
           <div>
             <h2 className="text-xl font-semibold text-neutral-900">
               {editMode ? 'Edit Task' : 'Create New Task'}
             </h2>
             <p className="text-sm text-neutral-600 mt-1">
               {editMode ? 'Modify the task details below' : 'Fill in the details to create a new task'}
             </p>
           </div>
         </ModalHeader>

        <ModalBody>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
                             {/* Task Name */}
               <div>
                 <label className="block text-sm font-medium text-neutral-700 mb-2">
                   Task Name <span className="text-error">*</span>
                 </label>
                 <Input
                   value={formData.Name}
                   onChange={(e) => handleInputChange('Name', e.target.value)}
                   placeholder={dataLoading ? "Loading..." : "Enter task name"}
                   isInvalid={!!errors.Name}
                   errorMessage={errors.Name}
                   className="w-full"
                   isDisabled={dataLoading}
                 />
               </div>

                             {/* Description */}
               <div>
                 <label className="block text-sm font-medium text-neutral-700 mb-2">
                   Description <span className="text-error">*</span>
                 </label>
                 <Textarea
                   value={formData.Description}
                   onChange={(e) => handleInputChange('Description', e.target.value)}
                   placeholder={dataLoading ? "Loading..." : "Describe what needs to be done"}
                   isInvalid={!!errors.Description}
                   errorMessage={errors.Description}
                   className="w-full"
                   minRows={3}
                   isDisabled={dataLoading}
                 />
               </div>

                             {/* Metric Selection */}
               <div>
                 <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Metric (Optional)
                 </label>
                 <Select
                   selectedKeys={formData.metric ? [formData.metric] : []}
                   onSelectionChange={(keys) => handleInputChange('metric', Array.from(keys)[0])}
                   placeholder={dataLoading ? "Loading metrics..." : "Select a metric"}
                   isInvalid={!!errors.metric}
                   errorMessage={errors.metric}
                   className="w-full"
                   isDisabled={dataLoading}
                 >
                   {dataLoading ? (
                     <SelectItem key="loading" value="loading">
                       Loading metrics...
                     </SelectItem>
                   ) : (
                     metrics.map((metric) => (
                       <SelectItem key={metric.id} value={metric.id}>
                         {metric.Name || 'Unnamed Metric'}
                       </SelectItem>
                     ))
                   )}
                 </Select>
               </div>

                             {/* Workflow Selection */}
               <div>
                 <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Workflow (Optional)
                 </label>
                 <p className="text-xs text-neutral-500 mb-2">
                   Select a workflow template to define the approval process for this task
                 </p>
                 <Select
                   selectedKeys={formData.workflowId ? [formData.workflowId] : []}
                   onSelectionChange={(keys) => handleWorkflowChange(Array.from(keys)[0])}
                   placeholder={dataLoading ? "Loading workflows..." : "Select workflow"}
                   isInvalid={!!errors.workflowId}
                   errorMessage={errors.workflowId}
                   className="w-full"
                   isDisabled={dataLoading}
                 >
                   {dataLoading ? (
                     <SelectItem key="loading" value="loading">
                       Loading workflows...
                     </SelectItem>
                   ) : (
                     workflows.map((workflow) => (
                       <SelectItem key={workflow.id} value={workflow.id}>
                         {workflow.attributes?.Name || workflow.Name || 'Unnamed Workflow'}
                       </SelectItem>
                     ))
                   )}
                 </Select>
               </div>






            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Status <span className="text-error">*</span>
                </label>
                <Select
                  selectedKeys={[formData.Status]}
                  onSelectionChange={(keys) => handleInputChange('Status', Array.from(keys)[0])}
                  className="w-full"
                  renderValue={(items) => {
                    return items.map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <Chip size="sm" color={getStatusColor(item.key)} variant="flat">
                          {item.key === 'not_started' ? 'Not Started' : 
                           item.key === 'in_progress' ? 'In Progress' :

                           item.key === 'completed' ? 'Completed' :
                           item.key === 'on_hold' ? 'On Hold' : item.key}
                        </Chip>
                      </div>
                    ));
                  }}
                >
                  <SelectItem key="not_started" value="not_started">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="default" variant="flat">Not Started</Chip>
                    </div>
                  </SelectItem>
                  <SelectItem key="in_progress" value="in_progress">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="primary" variant="flat">In Progress</Chip>
                    </div>
                  </SelectItem>

                  <SelectItem key="completed" value="completed">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="success" variant="flat">Completed</Chip>
                    </div>
                  </SelectItem>
                  <SelectItem key="on_hold" value="on_hold">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="danger" variant="flat">On Hold</Chip>
                    </div>
                  </SelectItem>
                </Select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Priority <span className="text-error">*</span>
                </label>
                <Select
                  selectedKeys={[formData.Priority]}
                  onSelectionChange={(keys) => handleInputChange('Priority', Array.from(keys)[0])}
                  className="w-full"
                  renderValue={(items) => {
                    return items.map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <Chip size="sm" color={getPriorityColor(item.key)} variant="flat">
                          {item.key === 'low' ? 'Low' : 
                           item.key === 'medium' ? 'Medium' :
                           item.key === 'high' ? 'High' :
                           item.key === 'critical' ? 'Critical' : item.key}
                        </Chip>
                      </div>
                    ));
                  }}
                >
                  <SelectItem key="low" value="low">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="success" variant="flat">Low</Chip>
                    </div>
                  </SelectItem>
                  <SelectItem key="medium" value="medium">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="primary" variant="flat">Medium</Chip>
                    </div>
                  </SelectItem>
                  <SelectItem key="high" value="high">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="warning" variant="flat">High</Chip>
                    </div>
                  </SelectItem>
                  <SelectItem key="critical" value="critical">
                    <div className="flex items-center gap-2">
                      <Chip size="sm" color="danger" variant="flat">Critical</Chip>
                    </div>
                  </SelectItem>
                </Select>
              </div>

                             {/* Start Date */}
               <div>
                 <label className="block text-sm font-medium text-neutral-700 mb-2">
                   Start Date <span className="text-error">*</span>
                 </label>
                                 <Input
                   type="date"
                   value={formData.StartDate}
                   onChange={(e) => handleDateChange('StartDate', e.target.value)}
                   isInvalid={!!errors.StartDate}
                   errorMessage={errors.StartDate}
                   className="w-full"
                   min={new Date().toISOString().split('T')[0]}
                   startContent={<FontAwesomeIcon icon={faCalendar} className="text-slate-400" />}
                 />
              </div>

                             {/* Estimated End Date */}
               <div>
                 <label className="block text-sm font-medium text-neutral-700 mb-2">
                   Estimated End Date <span className="text-error">*</span>
                 </label>
                <Input
                  type="date"
                  value={formData.EstimatedEndDate}
                  onChange={(e) => handleDateChange('EstimatedEndDate', e.target.value)}
                  isInvalid={!!errors.EstimatedEndDate}
                  errorMessage={errors.EstimatedEndDate}
                  className="w-full"
                  min={formData.StartDate || new Date().toISOString().split('T')[0]}
                  startContent={<FontAwesomeIcon icon={faCalendar} className="text-slate-400" />}
                />
              </div>



              {/* Progress */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Progress: {formData.Progress}%
                </label>
                <Slider
                  value={formData.Progress}
                  onChange={(value) => handleInputChange('Progress', value)}
                  minValue={0}
                  maxValue={100}
                  step={5}
                  className="w-full"
                  color="success"
                />
              </div>

              {/* Risk Level */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Risk Level
                </label>
                <Select
                  selectedKeys={[formData.RiskLevel]}
                  onSelectionChange={(keys) => handleInputChange('RiskLevel', Array.from(keys)[0])}
                  className="w-full"
                >
                  <SelectItem key="low" value="low">Low</SelectItem>
                  <SelectItem key="medium" value="medium">Medium</SelectItem>
                  <SelectItem key="high" value="high">High</SelectItem>
                </Select>
              </div>
            </div>
          </div>

          {/* Bottom Section - Full Width */}
          <Divider className="my-6" />
          
          <div className="space-y-4">
                         {/* Dependencies */}
             <div>
               <label className="block text-sm font-medium text-neutral-700 mb-2">
                 Dependencies
               </label>
               
                               {/* Search Bar */}
                <div className="mb-3">
                  <Input
                    placeholder={dataLoading ? "Loading tasks..." : "Search tasks by name or description..."}
                    value={dependencySearchTerm}
                    onChange={(e) => setDependencySearchTerm(e.target.value)}
                    className="w-full"
                    startContent={<FontAwesomeIcon icon={faChartLine} className="text-slate-400" />}
                    size="sm"
                    isDisabled={dataLoading}
                  />
                </div>
               
                               {/* Task List */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-3">
                  {dataLoading ? (
                    <div className="col-span-full text-center py-4 text-slate-500 text-sm">
                      Loading available tasks...
                    </div>
                  ) : filteredDependencyTasks.length > 0 ? (
                    filteredDependencyTasks.map((task) => (
                      <label key={task.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={(formData.Dependencies || []).includes(task.id)}
                          onChange={() => handleDependencyChange(task.id)}
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-slate-700 truncate">
                          {task.Name || task.name || 'Unnamed Task'}
                        </span>
                      </label>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-4 text-slate-500 text-sm">
                      {dependencySearchTerm ? 'No tasks found matching your search' : 'No tasks available'}
                    </div>
                  )}
                </div>
               
                               {/* Search Results Info */}
                {!dataLoading && dependencySearchTerm && (
                  <div className="mt-2 text-xs text-slate-500">
                    Showing {filteredDependencyTasks.length} of {availableTasks.length} tasks
                  </div>
                )}
             </div>

            {/* Simple Workflow Linking */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Workflow (Optional)
              </label>
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <p className="text-sm text-slate-600 mb-3">
                  Link this task to an approval workflow. Leave empty for simple task management.
                </p>
                                      <Select
                  selectedKeys={formData.workflowId ? [formData.workflowId] : []}
                  onSelectionChange={(keys) => handleWorkflowChange(Array.from(keys)[0])}
                  placeholder="Select workflow (optional)"
                      className="w-full"
                    >
                  {workflows.map((workflow) => (
                    <SelectItem key={workflow.id} value={workflow.id}>
                      {workflow.attributes?.Name || workflow.Name || workflow.name || 'Unnamed Workflow'}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Tags
              </label>
              <div className="space-y-2">
                <Input
                  placeholder="Type a tag and press Enter"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTagInput(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full"
                />
                {(formData.Tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.Tags.map((tag, index) => (
                      <Chip
                        key={index}
                        onClose={() => removeTag(tag)}
                        className="bg-emerald-100 text-emerald-700"
                      >
                        {tag}
                      </Chip>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Comments
              </label>
              <Textarea
                value={formData.Comments}
                onChange={(e) => handleInputChange('Comments', e.target.value)}
                placeholder="Additional notes or comments"
                className="w-full"
                minRows={2}
              />
            </div>
          </div>

                     {/* Error/Success Message */}
                       {errors.submit && (
              <div className={`mt-4 p-3 rounded-lg border ${
                errors.submit.includes("successfully") 
                  ? "bg-green-50 border-green-200" 
                  : "bg-red-50 border-red-200"
              }`}>
                <div className={`flex items-center gap-2 ${
                  errors.submit.includes("successfully") 
                    ? "text-green-700" 
                    : "text-red-700"
                }`}>
                  {errors.submit.includes("successfully") ? (
                    <FontAwesomeIcon icon={faCheckCircle} />
                  ) : (
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  )}
                  <span className="text-sm">
                    {errors.submit.includes("successfully") 
                      ? (editMode ? "Task updated successfully!" : "Task created successfully!")
                      : errors.submit
                    }
                  </span>
                </div>
              </div>
            )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="light"
            onClick={onClose}
            className="text-slate-600 hover:text-slate-800"
          >
            Cancel
          </Button>
                     <Button
             color="success"
             onClick={handleSubmit}
             isLoading={loading}
             startContent={!loading && <FontAwesomeIcon icon={faCheckCircle} />}
             className="bg-emerald-600 hover:bg-emerald-700"
           >
             {loading ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update Task" : "Create Task")}
           </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateTaskModal;